import { useMemo } from "react";
import { isEmpty, isUndefined } from "lodash";
import { Intent, InputGroup, TextArea } from "@blueprintjs/core";
import { ILayoutProps } from "../types";
import { evaluate, format } from "components/Template/action";
import { doHidden } from "components/Template/utils";
import { ILabel } from "components/Template/types";
import Validate from "components/Template/Validate";
import "./Label.scss";
import Markdown from "components/Markdown";
import { isTemplateRefreshing } from "components/Template/dependencyIndex";

export interface ILabelProps extends ILayoutProps {
  template: ILabel;
}

const getVariantClass = (variant: ILabel["variant"]) => {
  switch (variant) {
    case "small":
      return "small";
    case "large":
      return "large";
    case "area":
    case "text":
    default:
      return "";
  }
};

const getIntentClass = (invalid: boolean | undefined, verified: boolean | undefined) => {
  if (invalid) {
    return "invalid";
  } else if (verified) {
    return "verified";
  } else {
    return "";
  }
};

const Label = (props: ILabelProps) => {
  const { template, model, result, mutators, metadata } = props;
  const { getValue } = mutators;
  const value = useMemo(() => {
    if (template.format) {
      return format(model, template.format, getValue, result);
    } else {
      const tempValue = getValue(template.name) || result?.[template.name];
      return tempValue === 0 ? "0" : `${tempValue || ""}`;
    }
  }, [getValue, model, result, template.format, template.name]);

  if (doHidden(props)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  }

  if (metadata.hiddenInput.includes(template.name)) {
    metadata.hiddenInput = metadata.hiddenInput.filter((i) => i !== template.name);
  }

  const refreshing = isTemplateRefreshing(template, metadata, mutators.getPath);

  if (refreshing && (isUndefined(value) || isEmpty(value))) {
    return (
      <Validate
        {...props}
        className={`label ${getVariantClass(template.variant)} ${getIntentClass(undefined, undefined)} label--loading`}
        showLabel
        showInfo
      >
        <div className={`label__value-shell label__value-shell--${template.variant || "text"}`}>
          {template.variant === "markdown" ? (
            <div className="label__skeleton label__skeleton--markdown" aria-hidden="true">
              <span className="label__skeleton-line label__skeleton-line--markdown-1" />
              <span className="label__skeleton-line label__skeleton-line--markdown-2" />
              <span className="label__skeleton-line label__skeleton-line--markdown-3" />
            </div>
          ) : template.info && !["area", "input"].includes(template.variant || "text") ? (
            <div className="text bp3-ui-text bp3-text-large label__loading-inline">
              <span className={`label__skeleton-inline label__skeleton-inline--${template.variant || "text"}`} aria-hidden="true" />
              <span className="label__info-text">{template.info}</span>
            </div>
          ) : (
            <span className={`label__skeleton-line label__skeleton-line--${template.variant || "text"}`} aria-hidden="true" />
          )}
        </div>
      </Validate>
    );
  }

  if (isUndefined(value) || isEmpty(value)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  }

  const invalid = template.validate && !evaluate(model, template.validate, undefined, result);
  const verified = template.verify && evaluate(model, template.verify, undefined, result);
  const intent = invalid ? Intent.DANGER : verified ? Intent.SUCCESS : Intent.NONE;

  const renderLabel = () => {
    switch (template.variant) {
      case "area":
        return (
          <TextArea key={template.name} id={template.name} intent={intent} value={value} growVertically fill readOnly />
        );
      case "input":
        return (
          <InputGroup
            key={template.name}
            id={template.name}
            intent={intent}
            value={template.info ? `${value} ${template.info}` : value}
            readOnly
          />
        );
      case "markdown":
        return <div {...(template.width && { style: { width: `${template.width}px` }, })}><Markdown markdown={value} /></div>
      case "text":
      case "large":
      case "small":
      default:
        return (
          (template.info ? `${value} ${template.info}` : value).split("\n").map((item, index) => (
            <div className="text bp3-ui-text bp3-text-large" key={`${template.name}_${index}`}>
              {item}
            </div>
          ))
        );
    }
  };

  return (
    <Validate
      {...props}
      className={`label ${getVariantClass(template.variant)} ${getIntentClass(invalid, verified)}`}
      showLabel
      showInfo
    >
      {renderLabel()}
    </Validate>
  );
};

export default Label;
